import { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Divider
} from '@mui/material';
import { 
  DownloadOutlined, 
  PictureAsPdfOutlined, 
  TableChartOutlined,
  PrintOutlined
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useInventory } from '../contexts/InventoryContext';

/**
 * Inventory Report component that generates and exports various inventory reports
 */
const InventoryReport = () => {
  const { searchInventory, loading, error, clearError } = useInventory();
  const [reportType, setReportType] = useState('summary');
  const [warehouse, setWarehouse] = useState('All');
  const [dateRange, setDateRange] = useState({
    startDate: format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'), // 30 days ago
    endDate: format(new Date(), 'yyyy-MM-dd') // Today
  });
  const [reportData, setReportData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [warehouses] = useState(['All']); // TODO: Load real warehouses from API
  const tableRef = useRef(null);
  
  const reportTypes = [
    { value: 'summary', label: 'Inventory Summary' },
    { value: 'low-stock', label: 'Low Stock Items' },
    { value: 'activity', label: 'Inventory Activity' },
    { value: 'warehouse', label: 'Warehouse Comparison' }
  ];

  // Generate the report based on selected options
  const generateReport = async () => {
    setIsGenerating(true);
    setReportError(null);
    
    try {
      // Get all inventory data first
      const inventoryData = await searchInventory('');
      
      if (!inventoryData || !Array.isArray(inventoryData)) {
        throw new Error('Failed to retrieve inventory data');
      }
      
      // Filter data by warehouse if specified
      const filteredData = warehouse === 'All' 
        ? inventoryData 
        : inventoryData.filter(item => item.warehouse === warehouse);
      
      // Process data based on report type
      let processedData;
      
      switch (reportType) {
        case 'summary':
          processedData = generateSummaryReport(filteredData);
          break;
        case 'low-stock':
          processedData = generateLowStockReport(filteredData);
          break;
        case 'activity':
          processedData = generateActivityReport(filteredData, dateRange);
          break;
        case 'warehouse':
          processedData = generateWarehouseComparisonReport(inventoryData);
          break;
        default:
          processedData = { items: filteredData };
      }
      
      setReportData(processedData);
    } catch (err) {
      console.error('Error generating report:', err);
      setReportError(err.message || 'Failed to generate report');
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Generate inventory summary report
  const generateSummaryReport = (data) => {
    // Group items by category or type
    const categories = {};
    let totalItems = 0;
    let totalValue = 0;
    
    data.forEach(item => {
      // Extract category from description or use "Uncategorized"
      const category = extractCategory(item.description) || 'Uncategorized';
      
      if (!categories[category]) {
        categories[category] = {
          name: category,
          itemCount: 0,
          items: []
        };
      }
      
      categories[category].itemCount++;
      categories[category].items.push(item);
      
      totalItems++;
      // If item has value, calculate total value
      if (item.value) {
        totalValue += item.value;
      }
    });
    
    return {
      reportType: 'summary',
      generatedAt: new Date().toISOString(),
      warehouse: warehouse,
      totalItems,
      totalValue,
      categories: Object.values(categories).sort((a, b) => b.total_quantity - a.total_quantity),
      rawData: data
    };
  };
  
  // Generate low stock report
  const generateLowStockReport = (data) => {
    // Define low stock threshold
    const LOW_STOCK_THRESHOLD = 5;
    
    // Filter items with low stock
    const lowStockItems = data.filter(item => item.total_quantity <= LOW_STOCK_THRESHOLD && item.total_quantity > 0);
    // Out of stock items
    const outOfStockItems = data.filter(item => item.total_quantity <= 0);
    
    // Sort by quantity ascending
    lowStockItems.sort((a, b) => a.total_quantity - b.total_quantity);
    
    return {
      reportType: 'low-stock',
      generatedAt: new Date().toISOString(),
      warehouse: warehouse,
      lowStockThreshold: LOW_STOCK_THRESHOLD,
      lowStockItems,
      outOfStockItems,
      totalLowStockItems: lowStockItems.length,
      totalOutOfStockItems: outOfStockItems.length,
      rawData: data
    };
  };
  
  // Generate activity report
  const generateActivityReport = (data, range) => {
    const startDate = new Date(range.startDate);
    const endDate = new Date(range.endDate);
    endDate.setHours(23, 59, 59, 999); // End of the day
    
    // Filter transactions within date range
    const activities = [];
    
    data.forEach(item => {
      if (item.transactions) {
        item.transactions.forEach(transaction => {
          const transactionDate = new Date(transaction.timestamp);
          
          if (transactionDate >= startDate && transactionDate <= endDate) {
            activities.push({
              timestamp: transaction.timestamp,
              id: item.id,
              description: item.description,
              quantity: transaction.quantity,
              status: transaction.status,
              user: transaction.user,
              warehouse: item.warehouse
            });
          }
        });
      }
    });
    
    // Sort by timestamp descending (newest first)
    activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    
    // Calculate summary statistics
    const receivedCount = activities.filter(a => a.quantity > 0).reduce((sum, a) => sum + a.quantity, 0);
    const checkedOutCount = activities.filter(a => a.quantity < 0).reduce((sum, a) => sum + Math.abs(a.quantity), 0);
    
    return {
      reportType: 'activity',
      generatedAt: new Date().toISOString(),
      warehouse: warehouse,
      dateRange: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      },
      activities,
      totalActivities: activities.length,
      receivedCount,
      checkedOutCount,
      rawData: data
    };
  };
  
  // Generate warehouse comparison report
  const generateWarehouseComparisonReport = (data) => {
    // Group by warehouse
    const warehouseData = {};
    
    // Initialize with known warehouses
    warehouses.forEach(w => {
      if (w !== 'All') {
        warehouseData[w] = {
          name: w,
          itemCount: 0,
          total_quantity: 0,
          uniqueItems: 0,
          items: []
        };
      }
    });
    
    // Process data
    data.forEach(item => {
      const wh = item.warehouse || 'Main';
      
      if (!warehouseData[wh]) {
        warehouseData[wh] = {
          name: wh,
          itemCount: 0,
          total_quantity: 0,
          uniqueItems: 0,
          items: []
        };
      }
      
      warehouseData[wh].itemCount++;
      warehouseData[wh].total_quantity += item.total_quantity;
      warehouseData[wh].items.push(item);
    });
    
    // Calculate unique items count
    Object.keys(warehouseData).forEach(wh => {
      const uniqueItemCodes = new Set(warehouseData[wh].items.map(item => item.id));
      warehouseData[wh].uniqueItems = uniqueItemCodes.size;
    });
    
    return {
      reportType: 'warehouse',
      generatedAt: new Date().toISOString(),
      warehouses: Object.values(warehouseData),
      totalWarehouses: Object.keys(warehouseData).length,
      rawData: data
    };
  };
  
  // Extract category from item description
  const extractCategory = (description) => {
    if (!description) return 'Uncategorized';
    
    // Common categories in AV inventory
    const categories = [
      'Microphone', 'Cable', 'Speaker', 'Display', 'Projector', 
      'Camera', 'Mixer', 'Lighting', 'Computer', 'Adapter'
    ];
    
    for (const category of categories) {
      if (description.includes(category)) {
        return category;
      }
    }
    
    return 'Other';
  };
  
  // Export report to CSV
  const exportToCsv = () => {
    if (!reportData) return;
    
    let csvContent = '';
    let filename = '';
    
    // Create CSV based on report type
    switch (reportData.reportType) {
      case 'summary':
        csvContent = 'Category,Item Count,Total Quantity\n';
        reportData.categories.forEach(category => {
          csvContent += `"${category.name}",${category.itemCount},${category.total_quantity}\n`;
        });
        filename = `inventory-summary-${format(new Date(), 'yyyy-MM-dd')}.csv`;
        break;
        
      case 'low-stock':
        csvContent = 'ID,Description,Quantity,Warehouse\n';
        reportData.lowStockItems.forEach(item => {
          csvContent += `"${item.id}","${item.description}",${item.total_quantity},"${item.warehouse}"\n`;
        });
        filename = `low-stock-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
        break;
        
      case 'activity':
        csvContent = 'Timestamp,ID,Description,Quantity,Status,User,Warehouse\n';
        reportData.activities.forEach(activity => {
          csvContent += `"${activity.timestamp}","${activity.id}","${activity.description}",${activity.quantity},"${activity.status}","${activity.user}","${activity.warehouse}"\n`;
        });
        filename = `inventory-activity-${format(new Date(), 'yyyy-MM-dd')}.csv`;
        break;
        
      case 'warehouse':
        csvContent = 'Warehouse,Unique Items,Total Quantity\n';
        reportData.warehouses.forEach(warehouse => {
          csvContent += `"${warehouse.name}",${warehouse.uniqueItems},${warehouse.total_quantity}\n`;
        });
        filename = `warehouse-comparison-${format(new Date(), 'yyyy-MM-dd')}.csv`;
        break;
        
      default:
        return;
    }
    
    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Print report
  const printReport = () => {
    if (!tableRef.current) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>${getReportTitle()}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { text-align: center; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .report-info { margin-bottom: 20px; }
            .report-info p { margin: 5px 0; }
          </style>
        </head>
        <body>
          <h1>${getReportTitle()}</h1>
          <div class="report-info">
            <p><strong>Generated:</strong> ${format(new Date(), 'MMMM d, yyyy h:mm a')}</p>
            <p><strong>Warehouse:</strong> ${warehouse}</p>
            ${reportType === 'activity' ? `<p><strong>Date Range:</strong> ${dateRange.startDate} to ${dateRange.endDate}</p>` : ''}
          </div>
          ${tableRef.current.outerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };
  
  // Get report title based on type
  const getReportTitle = () => {
    const reportTypeObj = reportTypes.find(type => type.value === reportType);
    return reportTypeObj ? reportTypeObj.label : 'Inventory Report';
  };
  
  // Render the appropriate report based on type
  const renderReport = () => {
    if (!reportData) return null;
    
    switch (reportData.reportType) {
      case 'summary':
        return renderSummaryReport();
      case 'low-stock':
        return renderLowStockReport();
      case 'activity':
        return renderActivityReport();
      case 'warehouse':
        return renderWarehouseComparisonReport();
      default:
        return <Typography>No report data available</Typography>;
    }
  };
  
  // Render summary report
  const renderSummaryReport = () => {
    return (
      <>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Inventory Summary</Typography>
                <Typography variant="body2">
                  Total Items: <strong>{reportData.totalItems}</strong>
                </Typography>
                {reportData.totalValue > 0 && (
                  <Typography variant="body2">
                    Total Value: <strong>${reportData.totalValue.toFixed(2)}</strong>
                  </Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Categories: {reportData.categories.length}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {reportData.categories.slice(0, 5).map(category => (
                    <Chip 
                      key={category.name} 
                      label={`${category.name}: ${category.total_quantity}`}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  ))}
                  {reportData.categories.length > 5 && (
                    <Chip 
                      label={`+${reportData.categories.length - 5} more`}
                      color="default"
                      size="small"
                    />
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
        
        <TableContainer component={Paper}>
          <Table ref={tableRef}>
            <TableHead>
              <TableRow>
                <TableCell>Category</TableCell>
                <TableCell align="right">Unique Items</TableCell>
                <TableCell align="right">Total Quantity</TableCell>
                <TableCell align="right">% of Inventory</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.categories.map((category) => (
                <TableRow key={category.name}>
                  <TableCell component="th" scope="row">
                    {category.name}
                  </TableCell>
                  <TableCell align="right">{category.itemCount}</TableCell>
                  <TableCell align="right">{category.total_quantity}</TableCell>
                  <TableCell align="right">
                    {reportData.totalItems ? 
                      `${((category.total_quantity / reportData.totalItems) * 100).toFixed(1)}%` : 
                      '0%'
                    }
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };
  
  // Render low stock report
  const renderLowStockReport = () => {
    return (
      <>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Low Stock Alert</Typography>
                <Typography variant="body2">
                  Items below threshold ({reportData.lowStockThreshold}): <strong>{reportData.totalLowStockItems}</strong>
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3, bgcolor: reportData.totalOutOfStockItems > 0 ? '#fff4e5' : 'inherit' }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }} color={reportData.totalOutOfStockItems > 0 ? 'error' : 'inherit'}>
                  Out of Stock
                </Typography>
                <Typography variant="body2">
                  Items out of stock: <strong>{reportData.totalOutOfStockItems}</strong>
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
        
        <Typography variant="h6" gutterBottom>Low Stock Items</Typography>
        <TableContainer component={Paper}>
          <Table ref={tableRef}>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell>Warehouse</TableCell>
                <TableCell>Last Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.lowStockItems.map((item) => (
                <TableRow key={item.id} sx={{ bgcolor: item.total_quantity <= 2 ? '#fff4e5' : 'inherit' }}>
                  <TableCell component="th" scope="row">
                    {item.id}
                  </TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={item.total_quantity} 
                      color={item.total_quantity <= 2 ? 'error' : 'warning'} 
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{item.warehouse}</TableCell>
                  <TableCell>{format(new Date(item.last_updated), 'yyyy-MM-dd')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {reportData.outOfStockItems.length > 0 && (
          <>
            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Out of Stock Items</Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Warehouse</TableCell>
                    <TableCell>Last Updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.outOfStockItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell component="th" scope="row">
                        {item.id}
                      </TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>{item.warehouse}</TableCell>
                      <TableCell>{format(new Date(item.last_updated), 'yyyy-MM-dd')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </>
    );
  };
  
  // Render activity report
  const renderActivityReport = () => {
    return (
      <>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Total Activities</Typography>
                <Typography variant="body2">
                  <strong>{reportData.totalActivities}</strong> transactions
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }} color="success.main">Received</Typography>
                <Typography variant="body2">
                  <strong>{reportData.receivedCount}</strong> items received
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }} color="primary.main">Checked Out</Typography>
                <Typography variant="body2">
                  <strong>{reportData.checkedOutCount}</strong> items checked out
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
        
        <TableContainer component={Paper}>
          <Table ref={tableRef}>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Warehouse</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.activities.map((activity, index) => (
                <TableRow key={index}>
                  <TableCell>
                    {format(new Date(activity.timestamp), 'yyyy-MM-dd HH:mm')}
                  </TableCell>
                  <TableCell>{activity.id}</TableCell>
                  <TableCell>{activity.description}</TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={activity.quantity} 
                      color={activity.quantity > 0 ? 'success' : 'primary'} 
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{activity.status}</TableCell>
                  <TableCell>{activity.user}</TableCell>
                  <TableCell>{activity.warehouse}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };
  
  // Render warehouse comparison report
  const renderWarehouseComparisonReport = () => {
    return (
      <>
        <Box sx={{ mb: 3 }}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Warehouse Overview</Typography>
            <Typography variant="body2">
              Total Warehouses: <strong>{reportData.totalWarehouses}</strong>
            </Typography>
          </Paper>
        </Box>
        
        <TableContainer component={Paper}>
          <Table ref={tableRef}>
            <TableHead>
              <TableRow>
                <TableCell>Warehouse</TableCell>
                <TableCell align="right">Unique Items</TableCell>
                <TableCell align="right">Total Quantity</TableCell>
                <TableCell align="right">% of Total Inventory</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.warehouses.map((warehouse) => {
                const totalItems = reportData.warehouses.reduce((sum, wh) => sum + wh.total_quantity, 0);
                const percentage = totalItems > 0 ? (warehouse.total_quantity / totalItems) * 100 : 0;
                
                return (
                  <TableRow key={warehouse.name}>
                    <TableCell component="th" scope="row">
                      {warehouse.name}
                    </TableCell>
                    <TableCell align="right">{warehouse.uniqueItems}</TableCell>
                    <TableCell align="right">{warehouse.total_quantity}</TableCell>
                    <TableCell align="right">{percentage.toFixed(1)}%</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };
  
  return (
    <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        Inventory Reports
      </Typography>
      
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel id="report-type-label">Report Type</InputLabel>
            <Select
              labelId="report-type-label"
              id="report-type"
              value={reportType}
              label="Report Type"
              onChange={(e) => setReportType(e.target.value)}
              disabled={isGenerating}
            >
              {reportTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel id="warehouse-label">Warehouse</InputLabel>
            <Select
              labelId="warehouse-label"
              id="warehouse"
              value={warehouse}
              label="Warehouse"
              onChange={(e) => setWarehouse(e.target.value)}
              disabled={isGenerating || reportType === 'warehouse'}
            >
              {warehouses.map((wh) => (
                <MenuItem key={wh} value={wh}>{wh}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        {reportType === 'activity' && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                fullWidth
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                disabled={isGenerating}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="End Date"
                type="date"
                size="small"
                fullWidth
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                disabled={isGenerating}
              />
            </Grid>
          </>
        )}
      </Grid>
      
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={generateReport}
          disabled={isGenerating || loading}
          sx={{ px: 3, py: 1 }}
        >
          {isGenerating ? <CircularProgress size={24} color="inherit" /> : 'Generate Report'}
        </Button>
      </Box>
      
      {reportError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setReportError(null)}>
          {reportError}
        </Alert>
      )}
      
      {reportData && (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{getReportTitle()}</Typography>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadOutlined />}
                onClick={exportToCsv}
                sx={{ px: 2 }}
              >
                Export CSV
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PrintOutlined />}
                onClick={printReport}
                sx={{ px: 2 }}
              >
                Print
              </Button>
            </Box>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Generated on {format(new Date(), 'MMMM d, yyyy h:mm a')}
              {reportType !== 'warehouse' && ` • Warehouse: ${warehouse}`}
              {reportType === 'activity' && ` • Period: ${dateRange.startDate} to ${dateRange.endDate}`}
            </Typography>
          </Box>
          
          <Divider sx={{ mb: 3 }} />
          
          {renderReport()}
        </>
      )}
    </Paper>
  );
};

export default InventoryReport;